"use client";

import { useState } from "react";
import type { MemberTier } from "@/modules/identity/surfaces";
import {
  serviceLocationLabel,
  SERVICE_LOCATIONS,
  type ServiceLocation,
} from "@/modules/shared/serviceLocation";
import { ActionSubmit, Banner, FIELD_LABEL, Field } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * PractitionersScreen (docs/ux/screens/PractitionersScreen.md), shared by both
 * frontends (R22 T14).
 *
 * Five commands arrive as props. The rules they enforce stay on the server and
 * are worth naming, because this screen is where a clinic can lock itself out
 * or destroy work: demoting the last active manager is refused by the command
 * (H26), archiving protects existing Bookings rather than deleting them (H4),
 * and clearing them afterwards is a separate deliberate act that notifies every
 * Consumer. None of that is decided here.
 */
export interface PractitionerCommands {
  add: FormAction;
  archive: FormAction;
  setTier: FormAction;
  setServiceLocations: FormAction;
  clearBookings: FormAction;
}

export interface PractitionerView {
  id: string;
  displayName: string;
  isOwnerLabel: boolean;
  isYou: boolean;
  /** Service Locations this practitioner offers (H24). */
  serviceLocations: ServiceLocation[];
  /** Formatted date when archived, null while active. */
  archivedAt: string | null;
  /** Requested + Approved bookings still on this practitioner (H4). */
  activeBookingCount: number;
  /** Permission tier (H26); null if this practitioner has no membership. */
  tier: MemberTier | null;
}

/** H24: In-Clinic / At-Home checkboxes (name="serviceLocations"). */
function ServiceLocationCheckboxes({
  selected,
}: {
  selected: ServiceLocation[];
}) {
  return (
    <fieldset className="mt-3">
      <legend className={FIELD_LABEL}>
        Service locations
      </legend>
      <div className="flex flex-wrap gap-4">
        {SERVICE_LOCATIONS.map((loc) => (
          <label key={loc} className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              name="serviceLocations"
              value={loc}
              defaultChecked={selected.includes(loc)}
              className="h-4 w-4"
            />
            {serviceLocationLabel(loc)}
          </label>
        ))}
      </div>
      <p className="mt-1 text-xs text-muted">
        Leave both unchecked to default to In-Clinic.
      </p>
    </fieldset>
  );
}

export function PractitionersManager({
  practitioners,
  commands,
}: {
  practitioners: PractitionerView[];
  commands: PractitionerCommands;
}) {
  const active = practitioners.filter((p) => p.archivedAt === null);
  const archived = practitioners.filter((p) => p.archivedAt !== null);

  return (
    <>
      <AddPractitionerCard add={commands.add} />

      <ul className="space-y-3">
        {active.map((practitioner) => (
          <PractitionerRow
            key={practitioner.id}
            practitioner={practitioner}
            commands={commands}
          />
        ))}
      </ul>

      {archived.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wide text-muted">
            Archived
          </h2>
          <ul className="space-y-3">
            {archived.map((practitioner) => (
              <li
                key={practitioner.id}
                className="rounded-xl border border-line bg-surface p-5 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-ink">{practitioner.displayName}</p>
                  <span className="rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
                    Archived {practitioner.archivedAt}
                  </span>
                </div>
                {practitioner.activeBookingCount > 0 && (
                  <ClearBookings
                    practitioner={practitioner}
                    clearBookings={commands.clearBookings}
                  />
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Bulk action (H4): after archiving a practitioner, their existing bookings
 * are protected and remain, this clears them all at once (decline the pending,
 * cancel the approved). Consumers are notified of each. PractitionersScreen.
 */
function ClearBookings({
  practitioner,
  clearBookings,
}: {
  practitioner: PractitionerView;
  clearBookings: FormAction;
}) {
  const [confirming, setConfirming] = useState(false);
  const { state, pending, onSubmit } = useFormAction(async (form) => {
    const result = await clearBookings(form);
    if (result.success) setConfirming(false);
    return result;
  });

  return (
    <div className="mt-3 border-t border-line pt-3">
      {state.formError && <Banner>{state.formError}</Banner>}
      {state.success ? (
        <Banner tone="success">Their remaining bookings were cleared.</Banner>
      ) : !confirming ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-body">
            {practitioner.activeBookingCount} booking
            {practitioner.activeBookingCount === 1 ? "" : "s"} still need clearing.
          </p>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="shrink-0 text-sm font-semibold text-danger"
          >
            Clear them
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <input type="hidden" name="practitionerId" value={practitioner.id} />
          <p className="mb-3 text-sm text-body">
            Decline every pending request and cancel every confirmed booking for{" "}
            <span className="font-semibold">{practitioner.displayName}</span>? Each
            customer is notified. This can&apos;t be undone.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={pending}
              className="rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {pending ? "Clearing…" : "Clear all bookings"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-sm font-medium text-muted hover:text-ink"
            >
              Keep them
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function AddPractitionerCard({ add }: { add: FormAction }) {
  const [resetKey, setResetKey] = useState(0);
  // Clear the form after a successful add (element 7); a key remount is the
  // simplest reliable reset for uncontrolled fields. State updates happen
  // inside the action, not an effect (react-hooks/set-state-in-effect).
  const { state, pending, onSubmit } = useFormAction(async (form) => {
    const result = await add(form);
    if (result.success) setResetKey((k) => k + 1);
    return result;
  });

  return (
    <div className="mb-6 rounded-xl border border-line bg-surface p-6 shadow-sm">
      <h2 className="mb-1 font-bold text-ink">Add a practitioner</h2>
      <p className="mb-4 text-sm text-muted">
        We send them their login over WhatsApp. They get view-only access to
        bookings and customers; promote them to manager below for full access.
      </p>
      {state.formError && <Banner>{state.formError}</Banner>}
      {/*
        The success sentence comes from the command, not from here, and it is
        the SAME sentence in every branch (H43): a new account, somebody who
        already works at another clinic, and a number that turns out to belong
        to an admin all end on it. Rendering our own wording would be a place
        for those branches to become distinguishable again.
      */}
      {state.success && <Banner tone="success">{state.success}</Banner>}
      <form onSubmit={onSubmit} key={resetKey}>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="First name"
            name="firstName"
            defaultValue={state.values?.firstName}
            error={state.fieldErrors?.firstName}
          />
          <Field
            label="Last name"
            name="lastName"
            defaultValue={state.values?.lastName}
            error={state.fieldErrors?.lastName}
          />
        </div>
        <Field
          label="Phone number"
          name="phone"
          type="tel"
          defaultValue={state.values?.phone}
          error={state.fieldErrors?.phone}
          inputMode="tel"
          autoComplete="off"
          help="This is how they log in. We message them a password to get started."
        />
        <ServiceLocationCheckboxes selected={["in_clinic"]} />
        <ActionSubmit pendingLabel="Adding…" pending={pending}>Add practitioner</ActionSubmit>
      </form>
    </div>
  );
}

/**
 * H24 (J2#4b): edit which Service Locations a practitioner offers. Self-service,
 * no re-approval; narrowing protects existing Bookings (H4). Prefilled from the
 * current set; a plain "Save" persists the checked boxes.
 */
function ServiceLocationsEditor({
  practitioner,
  save,
}: {
  practitioner: PractitionerView;
  save: FormAction;
}) {
  const { state, pending, onSubmit } = useFormAction(save);

  return (
    <form onSubmit={onSubmit} className="mt-3 border-t border-line pt-3">
      <input type="hidden" name="practitionerId" value={practitioner.id} />
      <ServiceLocationCheckboxes selected={practitioner.serviceLocations} />
      {state.formError && (
        <div className="mt-2">
          <Banner>{state.formError}</Banner>
        </div>
      )}
      {state.success && (
        <div className="mt-2">
          <Banner tone="success">Service locations updated.</Banner>
        </div>
      )}
      <div className="mt-2">
        <ActionSubmit pendingLabel="Saving…" pending={pending}>Save locations</ActionSubmit>
      </div>
    </form>
  );
}

/**
 * H26: promote/demote a member's permission tier. Manager ⇄ Practitioner.
 * Demoting the last active manager is blocked by the command (lockout guard),
 * surfaced here as an inline error.
 */
function MemberTierControl({
  practitioner,
  setTier,
}: {
  practitioner: PractitionerView;
  setTier: FormAction;
}) {
  const { state, pending, onSubmit } = useFormAction(setTier);
  if (!practitioner.tier) return null;
  const isManager = practitioner.tier === "manager";
  const nextTier: MemberTier = isManager ? "practitioner" : "manager";

  return (
    <form onSubmit={onSubmit} className="mt-3 border-t border-line pt-3">
      <input type="hidden" name="practitionerId" value={practitioner.id} />
      <input type="hidden" name="tier" value={nextTier} />
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-body">
          {isManager
            ? "Full access, manages the business."
            : "View-only access to bookings and customers."}
        </p>
        <ActionSubmit pendingLabel="Saving…" pending={pending}>
          {isManager ? "Make practitioner" : "Make manager"}
        </ActionSubmit>
      </div>
      {state.formError && (
        <div className="mt-2">
          <Banner>{state.formError}</Banner>
        </div>
      )}
      {state.success && (
        <div className="mt-2">
          <Banner tone="success">Access updated.</Banner>
        </div>
      )}
    </form>
  );
}

function PractitionerRow({
  practitioner,
  commands,
}: {
  practitioner: PractitionerView;
  commands: PractitionerCommands;
}) {
  const [confirming, setConfirming] = useState(false);
  const { state, pending, onSubmit } = useFormAction(async (form) => {
    const result = await commands.archive(form);
    if (result.success) setConfirming(false);
    return result;
  });

  return (
    <li className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      {state.formError && <Banner>{state.formError}</Banner>}

      <div className="flex items-center justify-between gap-3">
        <p className="font-semibold text-ink">
          {practitioner.displayName}
          {practitioner.isYou && <span className="text-muted"> (you)</span>}
          {practitioner.isOwnerLabel && (
            <span className="ms-2 rounded-lg bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent">
              owner
            </span>
          )}
          {practitioner.tier && (
            <span
              className={`ms-2 rounded-lg px-2 py-0.5 text-xs font-semibold ${
                practitioner.tier === "manager"
                  ? "bg-accent/10 text-accent"
                  : "bg-line text-muted"
              }`}
            >
              {practitioner.tier === "manager" ? "Manager" : "Practitioner"}
            </span>
          )}
        </p>
        {!confirming && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="shrink-0 text-sm font-medium text-danger"
          >
            Archive
          </button>
        )}
      </div>

      {!confirming && (
        <MemberTierControl
          practitioner={practitioner}
          setTier={commands.setTier}
        />
      )}
      {!confirming && (
        <ServiceLocationsEditor
          practitioner={practitioner}
          save={commands.setServiceLocations}
        />
      )}

      {confirming && (
        <form onSubmit={onSubmit} className="mt-3 rounded-lg border border-line bg-page p-4">
          <input type="hidden" name="practitionerId" value={practitioner.id} />
          <p className="mb-3 text-sm text-body">
            Archive <span className="font-semibold">{practitioner.displayName}</span>?
            They take no new bookings and lose business access immediately.
            Existing bookings are not affected, decline or cancel those
            separately. Archiving can&apos;t be undone in this version.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={pending}
              className="rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {pending ? "Archiving…" : "Archive practitioner"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-sm font-medium text-muted hover:text-ink"
            >
              Keep them
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
